import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Row, Col, Typography, List,  Divider, Progress, Spin, Select, Statistic } from 'antd';
import { CheckCircleOutlined,  WarningOutlined, InfoCircleOutlined, TrophyOutlined, ClockCircleOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import TaskBar from './taskbar';
import { Column } from '@ant-design/plots';
import ExpandedResults from './ExpandedResults';

const { Title, Text, Paragraph } = Typography;

const defaultAttemptsOptions = [
    {
        value: 0,
        label: 'None',
    },
]

const AggregationType = {
  0: 'Daily',
  1: 'Weekly',
  2: 'Monthly',
  3: 'All Time',
};

const formatDate = (date) => {
  if (!date) return 'None';
  const attemptDate = new Date(date);
  const attemptTime = attemptDate.toLocaleTimeString();
  const attemptFormattedDate = attemptDate.toLocaleDateString();
  return `${attemptFormattedDate} ${attemptTime}`;
}

const HazmatProgress = (props) => {
    const { dataList, reportData, isLoading, getReportData, getSelfDataRequest, getUserAttemptsRequest, userAttempts, userAttemptsLoading, printRef, currentUserId, vr_app_session } = props;
    const navigate = useNavigate();
    const [attemptsOptions, setAttemptsOptions] = useState(defaultAttemptsOptions);

    const [dateRange, setDateRange] = useState([]);
    const [aggregationType, setAggregationType] = useState(0);
    const [timeTrainingChartData, setTimeTrainingChartData] = useState([]);
    const [averageScoreChartData, setAverageScoreChartData] = useState([]);
    const [localUserId, setLocalUserId] = useState('');
    const [localTrainingId, setLocalTrainingId] = useState('');
    const [attemptId, setAttemptId] = useState({value: vr_app_session, label: formatDate(props.dataList?.attemptResult?.attemptDate)});
    const [isFetchingNewData, setIsFetchingNewData] = useState(false);

    useEffect(() => {
        getReportData({
            userId: localUserId,
            trainingId: localTrainingId,
            aggregationType,
            startDate: dateRange[0]?.format('YYYY-MM-DD'),
            endDate: dateRange[1]?.format('YYYY-MM-DD'),
        });
     }, [vr_app_session, attemptId, getSelfDataRequest, getReportData, localUserId, localTrainingId, aggregationType, dateRange]);

    const renderComparisonTable = () => {
        if (!props.dataList || props.dataList.length === 0 ) {
            return <p>No comparison data available.</p>;
        }

        const attemptDate = new Date(props.dataList.attemptResult.attemptDate);
        const attemptTime = attemptDate.toLocaleTimeString();
        const attemptFormattedDate = attemptDate.toLocaleDateString();

        const { training, attemptResult } = props.dataList;
        const { event_results } = attemptResult;

        // const requiredEvents = event_results.filter(event => event.modifier === 'NONE');
        // const exceptionEvents = event_results.filter(event => event.modifier === 'EXCEPTION' && event.time > 0);
        // const optionalEvents = event_results.filter(event => event.modifier === 'NOT_REQUIRED');

        const tasksForTaskbar = event_results.filter(e => (e.modifier === 'NOT_REQUIRED' && e.time > 0) || (e.modifier === 'EXCEPTION' && e.time > 0) || (e.modifier === "NONE"));

        const renderTaskSection = (title, events, color) => {
            const requiredTasks = events.filter(e => e.modifier === 'NONE');
            const exceptions = events.filter(e => e.modifier === 'EXCEPTION');
            const optional = events.filter(e => e.modifier === 'NOT_REQUIRED');
            const tasksForTaskbar = events.filter(e => (e.modifier === 'NOT_REQUIRED' && e.time > 0) || (e.modifier === 'EXCEPTION' && e.time > 0) || (e.modifier === "NONE"));


            return (
                <div>
                    <div style={{display: 'flex', flexWrap: 'wrap'}}>
                        {tasksForTaskbar.sort((a, b) => {
                            if (a.time === 0 || b.time === 0) {
                                return a.expectedOrder - b.expectedOrder;
                            }
                            return a.time - b.time;
                        }).map(event => <TaskBar key={event.title} event={event}/>)}
                    </div>
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: "row",
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginTop: '8px'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start'
                        }}>
                            <Text type="secondary">You have
                                completed {requiredTasks.filter(e => e.success).length} of {requiredTasks.length} required
                                tasks.</Text>
                            <Text type="secondary">You
                                had {exceptions.filter(e => e.time > 0).length} of {exceptions.length} exceptions.
                                Exceptions count against your score.</Text>
                            <Text type="secondary">You
                                completed {optional.filter(e => e.time > 0).length} of {optional.length} optional
                                task(s).</Text>
                        </div>
                    </div>
                </div>
        );
      }

        return (
          <Card style={{  margin: '0 auto' }}>
            <Title level={2}>{training.title} Training Results</Title>
            <Row>
              <Col xs={24} md={12}>
                <Card bordered={false} bodyStyle={{ padding: '0px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Title level={4}>Training Outcomes</Title>
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Statistic
                          title="Overall Score"
                          value={Math.round(attemptResult.overall_score)}
                          prefix={<TrophyOutlined />}
                          valueStyle={{ color: '#3f8600' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Success Score"
                          value={attemptResult.successScore}
                          suffix="%"
                          precision={2}
                          valueStyle={{ color: attemptResult.success ? '#3f8600' : '#cf1322' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Total Time"
                          value={attemptResult.overall_time}
                          precision={2}
                          suffix="s"
                          prefix={<ClockCircleOutlined />}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Result"
                          value={attemptResult.success ? 'Pass' : 'Fail'}
                          valueStyle={{ color: attemptResult.success ? '#3f8600' : '#cf1322' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="User"
                          value={attemptResult.user.fullname}
                          prefix={<UserOutlined />}
                        />
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card bordered={false} bodyStyle={{ padding: '0px',  height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Title level={4}>Performance Summary</Title>
                    <List
                      size="small"
                      dataSource={[
                        {
                          title: '',
                          value: renderTaskSection('Required Tasks', event_results, '#000000'),

                        },
                        // {
                        //   title: 'Exceptions Occurred',
                        //   value: renderTaskSection('Exceptions (Affect Score)', exceptionEvents, '#faad14'),
                        //   icon: <WarningOutlined style={{ color: '#faad14' }} />,
                        // },
                        // {
                        //   title: 'Optional Tasks Completed',
                        //   value: renderTaskSection('Optional Tasks', optionalEvents, '#8c8c8c'),
                        //   icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
                        // },
                      ]}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={item.icon}
                            title={item.title}
                            description={item.value}
                          />
                        </List.Item>
                      )}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
              <Divider />
              <Row>
                  <ExpandedResults trainingData={tasksForTaskbar.sort((a, b) => {
                      if (a.time === 0 || b.time === 0) {
                          return a.expectedOrder - b.expectedOrder;
                      }
                      return a.time - b.time;
                  })}></ExpandedResults>
              </Row>
         </Card>
        );
    };

    const initialize = () => {
        setAttemptsOptions(defaultAttemptsOptions);
    }

    useEffect(() => { 
        initialize();
    }, [vr_app_session])

    const handleAggregationChange = (value) => {
      setAggregationType(value);
    };

    // useEffect(() => {
    //   console.log('reportData', reportData);
    //     if (reportData ) {
    //         const timeTrainingChartData = reportData.slice(-5).map(item => ({
    //             date: new Date(item.date).toLocaleDateString(),
    //             value: Math.ceil(item.totalTime / 60),
    //         })).reverse();
    //         setTimeTrainingChartData(timeTrainingChartData);
    //         const averageScoreChartData = reportData.slice(-5).map(item => ({
    //             date: new Date(item.date).toLocaleDateString(),
    //             value: Math.round(item.averageScore),
    //         })).reverse();
    //         setAverageScoreChartData(averageScoreChartData);
    //     }
    //  }, [vr_app_session, attemptId, getSelfDataRequest, reportData, localUserId, localTrainingId, aggregationType, dateRange]);

    const timeTrainingConfig = {
      data: timeTrainingChartData,
      xField: 'date',
      yField: 'value',
      height: 10,
      label: {
        position: 'middle',
        style: {
          fill: '#000',
          opacity: 1,
        },
      },
      xAxis: {
        label: {
          autoHide: true,
          autoRotate: false,
        },
      },
      meta: {
        date: {
          alias: 'Date',
        },
        value: {
          alias: 'Training Time (minutes)',
        },
      },
    };

    const averageScoreConfig = {
      data: averageScoreChartData,
      xField: 'date',
      yField: 'value',
      label: {
        position: 'middle',
        style: {
          fill: '#000',
          opacity: 1,
        },
        formatter: (value) => {
            return `${value.value.toFixed(0)}%`;
        }
      },
      xAxis: {
        label: {
          autoHide: true,
          autoRotate: false,
        },
      },
      meta: {
        date: {
          alias: 'Date',
        },
        value: {
          alias: 'Average Score',
        },
      },
    };

    useEffect(() => {
        if (currentUserId && dataList && dataList.training) {
            setLocalUserId(currentUserId);
            setLocalTrainingId(dataList.training.id);
        }
    }, [currentUserId, dataList]);

    useEffect(() => {
        if (reportData && reportData.reportItems) {
            const timeTrainingChartData = reportData.reportItems.slice(-5).map(item => ({
                date: new Date(item.date).toLocaleDateString(),
                value: Math.round(item.totalTime / 60),
            })).reverse();
            setTimeTrainingChartData(timeTrainingChartData);

            const averageScoreChartData = reportData.reportItems.slice(-5).map(item => ({
                date: new Date(item.date).toLocaleDateString(),
                value: Math.round(item.averageScore),
            })).reverse();
            setAverageScoreChartData(averageScoreChartData);

        }
    }, [reportData]);

    useEffect(() => {
        if (userAttempts && userAttempts.attempts && userAttempts.attempts.length > 0) {
            const options = userAttempts.attempts.map(attempt => ({
                value: attempt.attempt_id,
                label: `${new Date(attempt.attemptDate).toLocaleDateString()} ${new Date(attempt.attemptDate).toLocaleTimeString()}`
            }));
            setAttemptsOptions(options);
        }
    }, [userAttempts]);

    const handleAttemptIdChange = (value) => {
      setAttemptId(value);
      setIsFetchingNewData(true);
      getReportData({
        userId: currentUserId,
        trainingId: dataList.training.id,
        aggregationType,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
      });
      getSelfDataRequest({ vr_app_session: value });
      navigate(`/precimar_ulm_600_e/${value}`);
    };

    useEffect(() => {
      if (reportData) {
        const timeTrainingChartData = reportData.slice(-5).map(item => ({
          date: new Date(item.date).toLocaleDateString(),
          value: Math.ceil(item.totalTime / 60),
        })).reverse();
        setTimeTrainingChartData(timeTrainingChartData);

        const averageScoreChartData = reportData.slice(-5).map(item => ({
          date: new Date(item.date).toLocaleDateString(),
          value: Math.round(item.averageScore),
        })).reverse();
        setAverageScoreChartData(averageScoreChartData);

        setIsFetchingNewData(false);
      }
    }, [reportData]);

    return (
        <Spin spinning={isLoading || userAttemptsLoading}>


            <Row>
            <Col span={24}>
                    <Select
                        style={{ width: 200, marginBottom: 16 }}
                        value={aggregationType}
                        onChange={handleAggregationChange}
                    >
                        {Object.entries(AggregationType).map(([key, value]) => (
                            <Select.Option key={key} value={parseInt(key)}>
                                {value}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>  
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              
                <Col span={12} >
                    <h3>Training Time (Minutes)</h3>
                    <div style={{ height: 150 }}>
                        {isFetchingNewData ? (
                            <Spin />
                        ) : (
                            <Column {...timeTrainingConfig} />
                        )}
                    </div>
                </Col>
                <Col span={12} >
                    <h3>Average Score</h3>
                    <div style={{ height: 150 }}>
                        {isFetchingNewData ? (
                            <Spin />
                        ) : (
                            <Column {...averageScoreConfig} />
                        )}
                    </div>
                </Col>
            </Row>

            <Row ref={printRef}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Text strong>Attempt: </Text>
                    <Select
                        value={attemptId}
                        style={{ width: 200 }}
                        onChange={handleAttemptIdChange}
                        options={attemptsOptions}
                        loading={userAttemptsLoading}
                    />
                </Col>
            </Row>                
                <Col xs={24}>
                    {renderComparisonTable()}
                </Col>
            </Row>
        </Spin>
    );
}

export default HazmatProgress;